import { PayloadAction } from "@reduxjs/toolkit"
import { BikeFleetStateProps, BikeStatusProps, StatusEnum } from "../types/bikeFleetType"
import { includeInToObjectNormalizedValue, normalizeText, transformBikeFleet } from "../utils"
import { isNotEmpty } from "utils"

type SetSearchBikeFleetPayloadAction = PayloadAction<{ search: string; status: StatusEnum }>
type setToggleCheckedByCodeFnucyPayloadAction = PayloadAction<{ id_fnuci: string; status: string }>
type getFilteredBikeFleetPayloadAction = PayloadAction<{
	status: string
	noChangeStatusUpdatedMessage?: boolean
}>
type setToggleCheckedAllPayloadAction = PayloadAction<BikeStatusProps>

// Action to set the search state for the bike fleet.
export const setSearchBikeFleet = (
	state: BikeFleetStateProps,
	action: SetSearchBikeFleetPayloadAction
) => {
	const chains = normalizeText(action.payload.search)
	const filteredBikeFleet = state.bikeFleet.filter((bike) =>
		Number(action.payload.status) !== StatusEnum.ALL
			? bike.bike_status === Number(action.payload.status)
			: bike
	)
	const searchBikeFleet = transformBikeFleet(filteredBikeFleet).filter((bike) =>
		includeInToObjectNormalizedValue(bike, chains.trim())
	)
	state.searchBikeFLeet = action.payload.search
	state.filteredBikeFleet = isNotEmpty(action.payload.search)
		? searchBikeFleet
		: transformBikeFleet(filteredBikeFleet)
}

// Action to get the filtered bike fleet based on the current search and status.
export const getFilteredBikeFleet = (
	state: BikeFleetStateProps,
	action: getFilteredBikeFleetPayloadAction
) => {
	const filteredBikeFleet = state.bikeFleet.filter((bike) =>
		Number(action.payload.status) !== StatusEnum.ALL
			? bike.bike_status === Number(action.payload.status)
			: bike
	)
	const chains = normalizeText(state.searchBikeFLeet)

	const searchBikeFleet = transformBikeFleet(filteredBikeFleet).filter((bike) =>
		includeInToObjectNormalizedValue(bike, chains.trim())
	)
	state.filteredBikeFleet = isNotEmpty(state.searchBikeFLeet)
		? searchBikeFleet
		: transformBikeFleet(filteredBikeFleet)
}
// Action to toggle the selected status of a bike by its FNUCI code.
export const setToggleCheckedByCodeFnucy = (
	state: BikeFleetStateProps,
	action: setToggleCheckedByCodeFnucyPayloadAction
) => {
	const { status: bikeStatus, choice: chosenStatus } = state.filteredBikeFleet.find(
		(bike) => bike.fnuciCode === action.payload.id_fnuci
	)

	// Finds the bike by FNUCI code and toggles its 'choice' status.
	const numberOfBikesSelected = state.filteredBikeFleet.filter((bike) => bike.choice).length
	const numberOfBikesNotSelected = state.filteredBikeFleet.filter((bike) => !bike.choice).length

	// Add toggle choice in to background  bike fleet
	state.bikeFleet = state.bikeFleet.map((bike) =>
		bike.bike_id === action.payload.id_fnuci ? { ...bike, choice: !bike.choice } : bike
	)

	// Add toggle choice in to current view bike fleet
	state.filteredBikeFleet = state.filteredBikeFleet.map((bike) => ({
		...bike,
		choice: bike.fnuciCode === action.payload.id_fnuci ? !bike.choice : bike.choice,
	}))

	// Adjusts the selected type and 'all' selection based on the current selection.
	state.currentBikeFleetStatusSelected =
		numberOfBikesSelected === 0
			? (String(bikeStatus) as any)
			: numberOfBikesSelected === 1 && chosenStatus
				? false
				: state.currentBikeFleetStatusSelected

	state.selectBikeFleetAll =
		numberOfBikesNotSelected === 1 && !chosenStatus
			? (String(bikeStatus) as any)
			: numberOfBikesSelected === 1 && chosenStatus
				? false
				: numberOfBikesNotSelected === 0 && chosenStatus
					? false
					: state.selectBikeFleetAll
}

// Action to toggle the 'checked' status of all bikes.
export const setToggleCheckedAll = (
	state: BikeFleetStateProps,
	action: setToggleCheckedAllPayloadAction
) => {
	const hasNoSelectedBikes = state.filteredBikeFleet.filter((i) => !i.choice).length > 0
	const StatusAllChecked =
		action.payload === state.selectBikeFleetAll && !hasNoSelectedBikes ? false : action.payload

	state.selectBikeFleetAll = StatusAllChecked
	state.currentBikeFleetStatusSelected = StatusAllChecked
	state.filteredBikeFleet = state.filteredBikeFleet.map((bike) => ({
		...bike,
		choice: StatusAllChecked as boolean,
	}))
	state.bikeFleet = state.bikeFleet.map((bike) => {
		const getCorrespondingBike = state.filteredBikeFleet.find(
			(item) => item.fnuciCode === bike.bike_id
		)
		return getCorrespondingBike ? { ...bike, choice: StatusAllChecked as boolean } : bike
	})
}
export const resetUpdatedBikeStatus = (state: BikeFleetStateProps) => {
	state.updateBike.error = []
	state.updateBike.success = []
}
